import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { useProductStore } from '@/store/products'

export const useOnboardingStore = defineStore('onboardingStore', () => {
  const productStore = useProductStore()

  const onboarding = ref(null)
  const loading = ref(false)
  const error = ref(null)
  const currentOnboardingId = ref(null)
  const activeSource = ref('default')

  const products = computed(() =>
    productStore.getProductsWithSource(activeSource.value),
  )
  const mostPopularIndex = computed(() =>
    productStore.getMostPopularIndex(activeSource.value),
  )
  const defaultSelectedIndex = computed(() =>
    productStore.getDefaultSelectedIndex(activeSource.value),
  )

  const setActiveSource = source => {
    activeSource.value = source || 'default'
  }

  const productKeyAt = index =>
    productStore.getProductKeysForSource(activeSource.value)[index]

  const getDiscountIdForIndex = index => {
    const key = productKeyAt(index)
    if (!key) return ''
    return productStore.getDiscountIdForProductKey(activeSource.value, key)
  }

  const getDiscountRateForIndex = index => {
    const key = productKeyAt(index)
    if (!key) return 0
    return productStore.getDiscountRateForProductKey(activeSource.value, key)
  }

  const loadOnboarding = async onboardingId => {
    if (currentOnboardingId.value === onboardingId && onboarding.value) {
      return
    }
    loading.value = true
    error.value = null

    try {
      const module = await import(
        `@/constants/onboardings/${onboardingId}/index.js`
      )
      onboarding.value = module.default
      currentOnboardingId.value = onboardingId
    } catch (e) {
      console.warn(
        `Failed to load onboarding '${onboardingId}', falling back`,
        e,
      )
      try {
        const fallback = await import(
          '@/constants/onboardings/onboarding-1/index.js'
        )
        onboarding.value = fallback.default
        error.value = `Falling back to 'onboarding-1'`
      } catch (fallbackErr) {
        error.value = 'Failed to load any onboarding configuration'
        loading.value = false
        return
      }
    }

    try {
      await productStore.refetchProducts(onboarding.value?.paywall || 'default')
    } catch (e) {
      console.warn('Failed to load products via productStore:', e)
      error.value = 'Failed to load products'
    }

    loading.value = false
  }

  return {
    onboarding,
    products,
    mostPopularIndex,
    defaultSelectedIndex,
    activeSource,
    loading,
    error,
    setActiveSource,
    getDiscountIdForIndex,
    getDiscountRateForIndex,
    loadOnboarding,
  }
})
